import * as React from "react";

import { inject, observer } from "mobx-react";
import { observe } from "mobx";

import {
    Avatar,
    createStyles,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    PaletteType,
    Select,
    Theme,
    withStyles,
} from "@material-ui/core";

import TextField from "@material-ui/core/TextField";
import { PredefinedColor } from "meet-addons-sdk";
import { LocationSummary } from "../../domain/auxilary/LocationSummary";
import { UserRequestInfo } from "../../services/auth/UserRequestInfo";
import { IConfigStoreProps } from "../../stores/IConfigStoreProps";
import { PredefinedStoreName } from "../enums/PredefinedStoreName";
import CityPicker from "./CityPicker";

import { UserSummaryInfo } from "../../services/auth/UserSummaryInfo";

import i18n from "../../services/localization/i18n";

export interface IUserProfileProps {
    dir?: string;
    theme?: Theme;
    classes?: {
        avatar: string;
        container: string;
        divider: string;
        formControl: string;
        name: string;
        textField: string;
    };
}

const styles = (theme: Theme) => createStyles({
    avatar: {
        height: 50,
        marginLeft: theme.spacing(),
        width: 50,
    },
    container: {
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
    },
    divider: {
        maxWidth: 400,
    },
    formControl: {
        margin: theme.spacing(),
        maxWidth: 400,
        minWidth: 120,
    },
    name: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
    },
    textField: {
        flexGrow: 1,
        marginLeft: theme.spacing(),
        marginRight: theme.spacing(),
        maxWidth: 400,
    },
});

export interface IUserProfileProps {

    onUserProfileChanged: (user: UserRequestInfo, submit?: boolean) => void;
    onSubmit: () => void;
}

export interface IUserProfileState {

    email?: string;

    firstName?: string;

    familyName?: string;

    locale?: string;

    location?: LocationSummary;

    theme?: PaletteType;

    color?: PredefinedColor;
}

@inject(PredefinedStoreName.CONFIG)
@observer
class UserProfile extends React.Component<IUserProfileProps & IConfigStoreProps, IUserProfileState> {

    private isDirty: boolean = false;

    /**
     * Creates an instance of UserProfile.
     * @param {(IUserProfileProps & IConfigStoreProps)} props
     * @memberof UserProfile
     */
    constructor(props: IUserProfileProps & IConfigStoreProps & IUserProfileProps) {

        super(props);

        this.state = this.getMeState(this.props.configStore!.me!);

        observe(this.props.configStore!, "me", () => {
            this.setState(this.getMeState(this.props.configStore!.me!));
        });
    }

    public render() {

        const {classes} = this.props;

        return <form className={classes!.container} noValidate={false} autoComplete="off">
            <TextField
                autoFocus={true}
                className={classes!.textField}
                id="first-name"
                label={ i18n.t("userprofile_name_label") }
                margin="normal"
                onBlur={this.onSubmit}
                onChange={this.onFirstNameChanged}
                value={this.state.firstName}
                variant="outlined"
            />
            <TextField
                id="last-name"
                label={ i18n.t("userprofile_surname_label") }
                className={classes!.textField}
                value={this.state.familyName}
                onBlur={this.onSubmit}
                onChange={this.onLastNameChanged}
                margin="normal"
                variant="outlined"
            />

            <TextField
                id="email"
                label={ i18n.t("userprofile_email_label") }
                disabled={true}
                className={classes!.textField}
                value={this.state.email}
                onChange={this.onEmailChanged}
                onBlur={this.onSubmit}
                margin="normal"
                variant="outlined"
            />
            <Divider light={true} className={classes!.divider} />

            <CityPicker label={ i18n.t("userprofile_city_label") }
                        location={this.state.location}
                        margin="normal"
                        variant="outlined"
                        width={400}
                        fullWidth={false}
                        onSelect={this.onLocationSelected}
                        />

            <FormControl
                margin="normal"
                variant="outlined"
                fullWidth={true}
                className={classes!.formControl}>
                <InputLabel htmlFor="outlined-locale">
                    { i18n.t("userprofile_locale_label") }
                </InputLabel>
                <Select
                    fullWidth={true}
                    value={this.state.locale}
                    onChange={this.onLocaleChanged}
                    input={<OutlinedInput labelWidth={120} name="locale" id="outlined-loc" />}
                    >
                    <MenuItem value="en">{ i18n.t("language_en_title") }</MenuItem>
                    <MenuItem value="es">{ i18n.t("language_es_title") }</MenuItem>
                    <MenuItem value="de">{ i18n.t("language_de_title") }</MenuItem>
                    <MenuItem value="fr">{ i18n.t("language_fr_title") }</MenuItem>
                    <MenuItem value="it">{ i18n.t("language_it_title") }</MenuItem>
                    <MenuItem value="pl">{ i18n.t("language_pl_title") }</MenuItem>
                    <MenuItem value="sr-Latn">{ i18n.t("language_srLatn_title") }</MenuItem>
                    <MenuItem value="sr-Cyrl">{ i18n.t("language_srCyrl_title") }</MenuItem>
                </Select>
            </FormControl>

            <FormControl variant="outlined" className={classes!.formControl}>
                <InputLabel htmlFor="outlined-theme">
                    { i18n.t("userprofile_theme_label") }
                </InputLabel>
                <Select
                    value={this.state.theme}
                    onChange={this.onThemeChanged}
                    input={<OutlinedInput labelWidth={120} name="theme" id="outlined-theme" />}
                    >
                    <MenuItem value="dark">{ i18n.t("userprofile_themedark_label") }</MenuItem>
                    <MenuItem value="light">{ i18n.t("userprofile_themelight_label") }</MenuItem>
                </Select>
            </FormControl>
            <FormControl variant="outlined"  className={classes!.formControl} style={{
                display: "flex",
                flexDirection: "row",
                maxWidth: 455,
            }}>
                <InputLabel htmlFor="outlined-color">
                    { i18n.t("userprofile_color_label") }
                </InputLabel>
                <Select
                    value={this.state.color}
                    onChange={this.onColorChanged}
                    style={{
                        flexGrow: 1,
                    }}
                    input={<OutlinedInput labelWidth={120} name="color" id="outlined-color" />}
                    >
                        <MenuItem value={PredefinedColor.DARK_GREEN}>{ i18n.t("userprofile_label_green") }</MenuItem>
                        <MenuItem value={PredefinedColor.DARK_INDIGO}>{ i18n.t("userprofile_label_indigo") }</MenuItem>
                        <MenuItem value={PredefinedColor.DARK_PINK}>{ i18n.t("userprofile_label_pink") }</MenuItem>
                        <MenuItem value={PredefinedColor.DARK_TEAL}>{ i18n.t("userprofile_label_teal") }</MenuItem>
                        <MenuItem value={PredefinedColor.DARK_YELLOW}>{ i18n.t("userprofile_label_yellow") }</MenuItem>
                        <MenuItem value={PredefinedColor.DEEP_ORANGE}>{ i18n.t("userprofile_label_orange") }</MenuItem>
                        <MenuItem value={PredefinedColor.DEEP_PURPLE}>{ i18n.t("userprofile_label_purple") }</MenuItem>
                </Select>
                <Avatar className={classes!.avatar} style={{
                   backgroundColor: this.state.color!,
                }}>
                    {this.getInitials()}
                </Avatar>
            </FormControl>

        </form>;
    }

    private getMeState = (me: UserSummaryInfo) : IUserProfileState => {
        return {
            color: me.color,
            email: me.email,
            familyName: me.familyName,
            firstName: me.firstName,
            locale: me.locale,
            location: me.location,
            theme: me.theme,
        };
    }

    private onSubmit = () => {
        if (this.isDirty) {
            this.props.onSubmit();
        }

        this.isDirty = false;
    }

    private onColorChanged = (event: React.ChangeEvent<{name?: string | undefined, value: unknown}>,
                              _: React.ReactNode)  => {
            this.setState({
                color: event.target.value as PredefinedColor,
            }, () => {
                this.props.onUserProfileChanged(this.state, true);
            });
    }

    private onThemeChanged = (event: React.ChangeEvent<{name?: string | undefined, value: unknown}>,
                              _: React.ReactNode)  => {
        let theme: PaletteType;
        if (event.target.value === "dark") {
            theme = "dark";
        } else {
            theme = "light";
        }

        if (theme === this.state.theme) {
            return;
        }

        this.setState({
            theme,
        }, () => {
            this.props.onUserProfileChanged(this.state, true);
        });

    }

    private onLocaleChanged = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const {me, setLocale} = this.props.configStore!;
        
        if (event.target.value === this.state.locale) {
            return;
        }

        this.setState({
            locale: event.target.value as string,
        }, () => {
            
            setLocale(event.target.value as string);
            i18n.changeLanguage(me!.locale);
            this.props.onUserProfileChanged(this.state, true);
        });
    }

    private onLocationSelected = (location?: LocationSummary) => {;
        if (location === this.state.location) {
            return;
        }

        this.setState({
            location,
        }, () => {
            this.props.onUserProfileChanged(this.state, true);
        });
    }

    private onEmailChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === this.state.email) {
            return;
        }

        this.isDirty = true;

        this.setState({
            email: e.target.value,
        }, () => this.props.onUserProfileChanged(this.state, false));
    }

    private onLastNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === this.state.familyName) {
            return;
        }

        this.isDirty = true;

        this.setState({
            familyName: e.target.value,
        }, () => this.props.onUserProfileChanged(this.state, false));
    }

    private onFirstNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === this.state.firstName) {
            return;
        }

        this.isDirty = true;

        this.setState({
            firstName: e.target.value,
        }, () => this.props.onUserProfileChanged(this.state, false));
    }

    private getInitials() {
        let initials = this.state.firstName ? this.state.firstName.substr(0, 1) : "N/A";
        
        if (this.state.familyName) {
            const lastName = this.state.familyName.substr(0, 1);
            if (lastName !== "(") {
                initials += lastName;
            }
        }

        return initials;
    }

}

export default withStyles(styles, {withTheme: true})(UserProfile);
